// ============================================
// COSMIC HINDU - Product Card Component (Discount-aware)
// ============================================

import { Link } from 'react-router-dom';
import { useCart } from '@context/CartContext';
import { useWishlist } from '@context/WishlistContext';
import { useDiscounts } from '@context/DiscountContext';
import { formatPrice, getBadgeClass } from '@utils/helpers';

export default function ProductCard({ product }) {
  const { items, addItem, updateQty, removeItem } = useCart();
  const { toggle, isWishlisted } = useWishlist();
  const { discountedPrice } = useDiscounts();

  const cartItem = items.find(i => i.id === product.id);
  const qty = cartItem ? cartItem.qty : 0;

  const categoryId    = product.category_id || product.categoryId || product.cat;
  const originalPrice = product.price;
  const salePrice     = discountedPrice(originalPrice, categoryId);
  const hasSalePrice  = salePrice < originalPrice;

  // Show the bigger discount: either MRP→price or admin sitewide sale
  const displayPrice  = hasSalePrice ? salePrice : originalPrice;
  const comparePrice  = hasSalePrice ? originalPrice : (product.mrp || originalPrice);
  const discountPct   = comparePrice > displayPrice
    ? Math.round(((comparePrice - displayPrice) / comparePrice) * 100)
    : 0;

  return (
    <div className="card overflow-hidden group">
      {/* Image */}
      <Link to={`/product/${product.id}`} className="block relative overflow-hidden">
        <img
          src={product.images?.[0] || product.img}
          alt={product.name}
          className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-temple-100/80 to-transparent" />

        {product.badge && (
          <span className={`badge ${getBadgeClass(product.badge)} absolute top-3 left-3`}>
            {product.badge}
          </span>
        )}
        {hasSalePrice && (
          <span className="badge bg-emerald-500/90 text-white absolute top-3 left-3">SALE</span>
        )}

        <button
          onClick={(e) => { e.preventDefault(); toggle(product.id); }}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 border-none flex items-center justify-center cursor-pointer text-lg transition-colors"
        >
          {isWishlisted(product.id) ? '❤️' : '🤍'}
        </button>
      </Link>

      {/* Info */}
      <div className="p-4">
        <Link to={`/product/${product.id}`} className="no-underline">
          <h3 className="text-sm font-semibold text-temple-cream mb-1.5 leading-snug hover:text-gold-500 transition-colors">
            {product.name}
          </h3>
        </Link>

        <div className="flex items-center gap-1 mb-2.5">
          <span className="text-gold-400 text-sm tracking-wider">
            {'★'.repeat(Math.floor(product.rating || 4))}
            {'☆'.repeat(5 - Math.floor(product.rating || 4))}
          </span>
          <span className="text-white/40 text-xs font-sans ml-1">
            {product.rating || 4.5} ({product.reviews || 0})
          </span>
        </div>

        <div className="flex items-center gap-2 mb-3.5">
          <span className="text-xl font-bold text-gold-500 font-display">
            {formatPrice(displayPrice)}
          </span>
          {discountPct > 0 && (
            <>
              <span className="text-sm text-white/35 line-through">
                {formatPrice(comparePrice)}
              </span>
              <span className="text-[11px] text-emerald-400 font-bold font-sans">
                {discountPct}% off
              </span>
            </>
          )}
        </div>

        {qty > 0 ? (
          <div className="flex items-center justify-between w-full rounded overflow-hidden"
               style={{ border: '1px solid rgba(255,184,0,.4)' }}>
            <button
              onClick={() => qty === 1 ? removeItem(product.id) : updateQty(product.id, qty - 1)}
              className="px-4 py-2.5 text-base font-bold transition-colors"
              style={{ background: 'rgba(255,107,0,.15)', color: '#FFB800', border: 'none', cursor: 'pointer' }}
            >
              −
            </button>
            <span className="text-sm font-bold font-display" style={{ color: '#FFE082' }}>
              {qty}
            </span>
            <button
              onClick={() => updateQty(product.id, qty + 1)}
              className="px-4 py-2.5 text-base font-bold transition-colors"
              style={{ background: 'rgba(255,107,0,.15)', color: '#FFB800', border: 'none', cursor: 'pointer' }}
            >
              +
            </button>
          </div>
        ) : (
          <button
            onClick={() => addItem({ ...product, price: displayPrice })}
            className="btn-primary w-full py-2.5 text-[11px]"
          >
            Add to Cart
          </button>
        )}
      </div>
    </div>
  );
}
